/**
 * HostView — the dedicated host load/health surface (W4 frontend).
 *
 * Answers one operator question: "is this box busy, and can it take more
 * trials?" It reads the daemon's `GET /metrics` snapshot (polled by the App) and
 * renders two things:
 *   - a load/CPU time-series line graph over the rolling `history[]` window, so
 *     pressure over the last hour is legible at a glance; and
 *   - a full health panel — CPU%, 1-min load, free RAM, free disk, active/queued
 *     trials, image-build pressure, degradations/failures, and the concurrency
 *     cap with its headroom (`cap − active`).
 *
 * Off-happy-path is first-class: before the first sample the daemon answers
 * `503 { available: false }`, surfaced here as a calm waiting state (NOT an
 * error); a real fetch failure shows an honest error naming `/metrics`; a
 * mid-session failure keeps the last snapshot behind a stale banner; and any null
 * gauge renders an em dash rather than a misleading zero. Purely presentational
 * — props in, JSX out; the App owns the poll.
 */

import { type GraphScale, niceMax, seriesSegments } from './host-graph'
import {
  type MetricsReady,
  type MetricsSnapshot,
  type ResourceSample,
  formatDisk,
  formatImagePressure,
  formatLoad,
  formatPercent,
  formatRamMb,
  metricsHeadroom,
} from './metrics'

/** A surfaced read error (endpoint + message), or null. */
type ReadError = { endpoint: string; message: string } | null

export interface HostViewProps {
  /** Latest decoded `/metrics` snapshot, or null before the first poll lands. */
  metrics: MetricsSnapshot | null
  /** A retained `/metrics` error to surface, or null. */
  error: ReadError
}

export function HostView({ metrics, error }: HostViewProps): JSX.Element {
  return (
    <div className="host-view" data-testid="host-view">
      <div className="host-view-toolbar">
        <h1 className="host-view-title">Host</h1>
      </div>
      <div className="host-view-body">{renderBody(metrics, error)}</div>
    </div>
  )
}

/** Pick the off-happy-path state or the full surface from the current snapshot. */
function renderBody(metrics: MetricsSnapshot | null, error: ReadError): JSX.Element {
  // Nothing decoded yet: a first-poll failure is the only true thing to show;
  // otherwise we are still loading.
  if (metrics === null) {
    return error ? (
      <ErrorState endpoint={error.endpoint} message={error.message} />
    ) : (
      <LoadingState label={'loading host metrics\u2026'} />
    )
  }
  // The daemon has not captured a sample yet — a calm wait, not an error.
  if (!metrics.available) {
    return <PendingState reason={metrics.reason} />
  }
  return (
    <>
      {error ? <StaleBanner endpoint={error.endpoint} /> : null}
      <HostLoadGraph history={metrics.history} />
      <HostPanel snapshot={metrics} />
    </>
  )
}

// ─── Time-series graph ──────────────────────────────────────────────────

const GRAPH_W = 600
const GRAPH_H = 160
const GRAPH_PAD = 8
/** CPU% is a fixed 0–100 domain; load average auto-scales to its peak. */
const CPU_SCALE: GraphScale = { width: GRAPH_W, height: GRAPH_H, pad: GRAPH_PAD, max: 100 }
const GRAPH_VIEWBOX = `0 0 ${GRAPH_W} ${GRAPH_H}`

/**
 * The load/CPU line graph over the rolling sample window. CPU% (0–100, primary)
 * and 1-min load average (auto-scaled, secondary) are drawn as separate
 * polylines that break around any null reading so a failed probe reads as a gap,
 * not a dip to zero. An empty/all-null window renders a flat baseline — honest
 * "no trend yet", not an error.
 */
export function HostLoadGraph({ history }: { history: readonly ResourceSample[] }): JSX.Element {
  const cpu = history.map((s) => s.host.cpuPercent)
  const load = history.map((s) => s.host.loadAverage)
  const loadMax = niceMax(load)
  const cpuSegments = seriesSegments(cpu, CPU_SCALE)
  const loadSegments = seriesSegments(load, { ...CPU_SCALE, max: loadMax })
  const hasTrend = cpuSegments.length > 0 || loadSegments.length > 0

  return (
    <figure className="host-graph" data-testid="host-graph" data-samples={history.length}>
      <figcaption className="host-graph-legend">
        <span className="host-graph-key host-graph-key--cpu">CPU %</span>
        <span className="host-graph-key host-graph-key--load">load 1m (peak {loadMax})</span>
        <span className="host-graph-window tabular">{history.length} samples</span>
      </figcaption>
      <svg
        className="host-graph-svg"
        viewBox={GRAPH_VIEWBOX}
        preserveAspectRatio="none"
        role="img"
        aria-label="host CPU and load over time"
      >
        {hasTrend ? (
          <>
            {loadSegments.map((points, i) => (
              <polyline
                // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional slices of one ordered series
                key={`load-${i}`}
                points={points}
                className="host-graph-line host-graph-line--load"
                data-testid="host-graph-load"
              />
            ))}
            {cpuSegments.map((points, i) => (
              <polyline
                // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional slices of one ordered series
                key={`cpu-${i}`}
                points={points}
                className="host-graph-line host-graph-line--cpu"
                data-testid="host-graph-cpu"
              />
            ))}
          </>
        ) : (
          <line
            x1="0"
            y1={GRAPH_H / 2}
            x2={GRAPH_W}
            y2={GRAPH_H / 2}
            className="host-graph-baseline"
            data-testid="host-graph-flat"
          />
        )}
      </svg>
    </figure>
  )
}

// ─── Health panel ───────────────────────────────────────────────────────

/** The full metric set, the operator's at-a-glance load/headroom read. */
function HostPanel({ snapshot }: { snapshot: MetricsReady }): JSX.Element {
  const { host, trials, images } = snapshot.sample
  const headroom = metricsHeadroom(snapshot)
  const cap = snapshot.cap.maxConcurrentTrials
  return (
    <div className="host-panel" data-testid="host-panel">
      <section className="host-panel-group" aria-label="resources">
        <h2 className="host-panel-heading">Resources</h2>
        <dl className="host-stat-grid">
          <HostStat id="cpu" label="CPU" value={formatPercent(host.cpuPercent)} />
          <HostStat id="load" label="load (1m)" value={formatLoad(host.loadAverage)} />
          <HostStat id="ram" label="free RAM" value={formatRamMb(host.freeRamMb)} />
          <HostStat id="disk" label="free disk" value={formatDisk(host.freeDiskGb, host.freeDiskPercent)} />
        </dl>
      </section>

      <section className="host-panel-group" aria-label="capacity">
        <h2 className="host-panel-heading">Capacity</h2>
        <dl className="host-stat-grid">
          <HostStat id="active" label="active trials" value={String(trials.active)} />
          <HostStat id="queued" label="queued trials" value={String(trials.queued)} />
          <HostStat id="reconcile" label="awaiting reconcile" value={String(trials.awaitingReconcile)} />
          <HostStat id="cap" label="concurrency cap" value={String(cap)} />
          <HostStat id="headroom" label="headroom" value={String(headroom)} tone={headroom <= 0 ? 'warn' : 'ok'} />
        </dl>
      </section>

      <section className="host-panel-group" aria-label="images">
        <h2 className="host-panel-heading">Images</h2>
        <p className="host-panel-line tabular" data-testid="host-images">
          {formatImagePressure(images)}
        </p>
      </section>

      <HostHealth degraded={snapshot.sample.degraded} failures={snapshot.sample.failures} />
    </div>
  )
}

/** One labelled stat cell; `tone` tints the value for the headroom verdict. */
function HostStat({
  id,
  label,
  value,
  tone,
}: {
  id: string
  label: string
  value: string
  tone?: 'ok' | 'warn'
}): JSX.Element {
  return (
    <div className="host-stat" data-testid={`host-stat-${id}`}>
      <dt className="host-stat-label">{label}</dt>
      <dd className="host-stat-value tabular" data-tone={tone}>
        {value}
      </dd>
    </div>
  )
}

/** Degradations/failures line — calm `host healthy` when clean, loud when not. */
function HostHealth({ degraded, failures }: { degraded: boolean; failures: string[] }): JSX.Element {
  if (!degraded && failures.length === 0) {
    return (
      <p className="host-panel-health host-panel-health--ok" data-testid="host-health">
        host healthy — all probes reporting
      </p>
    )
  }
  return (
    <p className="host-panel-health host-panel-health--warn" role="alert" data-testid="host-health">
      degraded — {failures.length > 0 ? failures.join(', ') : 'a probe failed this sample'}
    </p>
  )
}

// ─── Off-happy-path states ──────────────────────────────────────────────

function LoadingState({ label }: { label: string }): JSX.Element {
  return (
    <div className="state state-loading" data-testid="host-loading">
      <p className="state-title">{label}</p>
    </div>
  )
}

function PendingState({ reason }: { reason: string }): JSX.Element {
  return (
    <div className="state state-empty" data-testid="host-pending">
      <p className="state-title">waiting for the first host sample</p>
      <p className="state-detail">{reason}</p>
    </div>
  )
}

function ErrorState({ endpoint, message }: { endpoint: string; message: string }): JSX.Element {
  return (
    <div className="state state-error" role="alert" data-testid="host-error">
      <p className="state-title">could not reach {endpoint}</p>
      <p className="state-detail">{message}</p>
    </div>
  )
}

function StaleBanner({ endpoint }: { endpoint: string }): JSX.Element {
  return (
    <div className="state-banner" role="alert" data-testid="host-banner">
      lost contact with {endpoint} — showing the last snapshot
    </div>
  )
}
